/**
 * The interviewer card's own edits, as pure functions.
 *
 * Toggling a section, stepping its minutes, dragging it, cycling a skill:
 * these change the config on the page at once, with no request, because the
 * recruiter is shaping a draft. Rio validates the whole config again on save.
 * Plain-language edits are different: those go to Rio (see useInterviewer).
 *
 * The total length is the recruiter's number. Sections share it, so turning one
 * off hands its minutes to the others, and moving the length slider rescales
 * them all. Stepping one section's minutes is the one edit that changes the
 * total, within 15 to 45 minutes.
 */
import {
  QUESTION_SECTIONS,
  WORKSPACE_KINDS,
  type CustomQuestion,
  type DiffEntry,
  type DraftedBar,
  type ExpectationLevel,
  type FieldError,
  type InterviewerConfig,
  type InterviewerPreview,
  type InterviewerSection,
  type PostingSkill,
  type PreviewSection,
  type SectionKey,
  type Tone,
  type WorkspaceKind,
  type WorkspaceTask,
} from '../services/interviewer.types';

export const MIN_TOTAL = 15;
export const MAX_TOTAL = 45;
export const MIN_SECTION = 1;
export const MAX_SECTION = 30;
export const MAX_QUESTIONS = 5;
export const MAX_QUESTION_LENGTH = 300;
export const MAX_AVOID_TOPICS = 10;
export const MAX_AVOID_TOPIC_LENGTH = 80;
/** Rio takes at most five skills of more focus, and five of less. */
export const MAX_FOCUS = 5;

/** Titles and one-line summaries, used until Rio's preview supplies its own. */
export const SECTION_META: Record<
  SectionKey,
  { title: string; summary: string }
> = {
  logistics: {
    title: 'The practical side',
    summary:
      'Location, schedule, pay and work authorisation, so nothing surprises anyone later.',
  },
  skills: {
    title: 'Skills and experience',
    summary:
      "Real situations from the role, aimed at what the résumé doesn't show.",
  },
  workspace: {
    title: 'Work task',
    summary:
      'A short task from the job, worked with an AI assistant beside them.',
  },
  ai_fluency: {
    title: 'How they work with AI',
    summary:
      'What they hand to AI, how they check it and what they keep for themselves.',
  },
  candidate_questions: {
    title: 'Their questions',
    summary:
      'Rio answers from the posting and flags anything it can’t answer for you.',
  },
};

export const enabledTotal = (sections: InterviewerSection[]): number =>
  sections.filter((s) => s.enabled).reduce((sum, s) => sum + s.minutes, 0);

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/**
 * Shares `target` minutes across the enabled sections in proportion to their
 * current minutes, as whole minutes that add up exactly (largest remainder),
 * each from one to thirty minutes, as Rio allows. A section that would pass
 * thirty stops there and the others share what's left; only when every
 * enabled section is full is the sum short of the target. Disabled sections
 * keep their minutes, so turning one back on restores roughly what it had.
 */
export const rebalance = (
  sections: InterviewerSection[],
  target: number,
): InterviewerSection[] => {
  const enabled = sections.filter((s) => s.enabled);
  if (!enabled.length) return sections;
  const room = MAX_SECTION - MIN_SECTION;
  const weights = enabled.map((s) => Math.max(s.minutes, MIN_SECTION));
  const spare = clamp(
    target - enabled.length * MIN_SECTION,
    0,
    enabled.length * room,
  );
  // Each section's exact share above its first minute; a full one is fixed
  // at thirty and the rest is shared again among the others.
  const exact = weights.map(() => 0);
  let open = weights.map((_, index) => index);
  let left = spare;
  for (;;) {
    const weightTotal = open.reduce((sum, i) => sum + weights[i], 0);
    const full = open.filter((i) => (left * weights[i]) / weightTotal > room);
    if (!full.length) {
      open.forEach((i) => {
        exact[i] = (left * weights[i]) / weightTotal;
      });
      break;
    }
    full.forEach((i) => {
      exact[i] = room;
      left -= room;
    });
    open = open.filter((i) => !full.includes(i));
  }
  const whole = exact.map(Math.floor);
  let rest = spare - whole.reduce((a, b) => a + b, 0);
  const byRemainder = exact
    .map((value, index) => ({ index, part: value - Math.floor(value) }))
    .sort((a, b) => b.part - a.part);
  for (const { index } of byRemainder) {
    if (rest <= 0) break;
    if (whole[index] >= room) continue;
    whole[index] += 1;
    rest -= 1;
  }
  let i = 0;
  return sections.map((s) =>
    s.enabled ? { ...s, minutes: MIN_SECTION + whole[i++] } : s,
  );
};

export const setTotal = (
  config: InterviewerConfig,
  minutes: number,
): InterviewerConfig => {
  const target = clamp(Math.round(minutes), MIN_TOTAL, MAX_TOTAL);
  return {
    ...config,
    targetMinutes: target,
    sections: rebalance(config.sections, target),
  };
};

/** False for the last section still on: an interview needs at least one. */
/**
 * Om's decision (27 Oct): every candidate gets to ask their own questions, and
 * every interview covers the role's skills. Rio rejects a config that turns
 * either off. The candidate's questions also stay last.
 */
export const ALWAYS_ON: ReadonlySet<SectionKey> = new Set<SectionKey>([
  'skills',
  'candidate_questions',
]);
export const FIXED_LAST: SectionKey = 'candidate_questions';

/** The always-on sections switched on, and the candidate's questions last. */
export const pinSections = (
  sections: InterviewerSection[],
): InterviewerSection[] => {
  const on = sections.map((s) =>
    ALWAYS_ON.has(s.key) && !s.enabled ? { ...s, enabled: true } : s,
  );
  const last = on.find((s) => s.key === FIXED_LAST);
  return last ? [...on.filter((s) => s.key !== FIXED_LAST), last] : on;
};

/** The last position a section can be dragged to: above the candidate's questions. */
export const lastMovableIndex = (config: InterviewerConfig) =>
  config.sections[config.sections.length - 1]?.key === FIXED_LAST
    ? config.sections.length - 2
    : config.sections.length - 1;

/** Why a section holding the team's own questions can't be turned off. */
export const HELD_LINE =
  'Your questions are asked here. Move or remove them first to turn it off.';

/** Whether questions of the team's own are asked in this section. */
export const holdsQuestions = (config: InterviewerConfig, key: SectionKey) =>
  config.customQuestions.some((q) => q.section === key);

/**
 * A section that holds the team's own questions stays on: Rio refuses a
 * question in a section that's off, so they're moved or removed first.
 */
export const canToggleOff = (config: InterviewerConfig, key: SectionKey) =>
  !ALWAYS_ON.has(key) &&
  !holdsQuestions(config, key) &&
  config.sections.filter((s) => s.enabled && s.key !== key).length > 0;

export const toggleSection = (
  config: InterviewerConfig,
  key: SectionKey,
): InterviewerConfig => {
  const section = config.sections.find((s) => s.key === key);
  if (!section) return config;
  if (section.enabled && !canToggleOff(config, key)) return config;
  const sections = config.sections.map((s) =>
    s.key === key ? { ...s, enabled: !s.enabled, minutes: s.minutes || 5 } : s,
  );
  return {
    ...config,
    sections: rebalance(sections, config.targetMinutes),
    workspace:
      key === 'workspace'
        ? { ...config.workspace, enabled: !section.enabled }
        : config.workspace,
  };
};

export const canStep = (
  config: InterviewerConfig,
  key: SectionKey,
  delta: number,
): boolean => {
  const section = config.sections.find((s) => s.key === key);
  if (!section?.enabled) return false;
  const next = section.minutes + delta;
  const total = enabledTotal(config.sections) + delta;
  return (
    next >= MIN_SECTION &&
    next <= MAX_SECTION &&
    total >= MIN_TOTAL &&
    total <= MAX_TOTAL
  );
};

export const stepSection = (
  config: InterviewerConfig,
  key: SectionKey,
  delta: number,
): InterviewerConfig => {
  if (!canStep(config, key, delta)) return config;
  const sections = config.sections.map((s) =>
    s.key === key ? { ...s, minutes: s.minutes + delta } : s,
  );
  return { ...config, sections, targetMinutes: enabledTotal(sections) };
};

export const moveSection = (
  config: InterviewerConfig,
  from: number,
  to: number,
): InterviewerConfig => {
  const last = lastMovableIndex(config);
  const target = clamp(to, 0, last);
  if (from === target || from < 0 || from > last) return config;
  const sections = [...config.sections];
  const [moved] = sections.splice(from, 1);
  sections.splice(target, 0, moved);
  return { ...config, sections };
};

export type SkillFocus = 'neutral' | 'more' | 'less';

/** Rio matches a skill's name without regard to case. */
const sameSkill = (a: string) => (b: string) =>
  a.toLowerCase() === b.toLowerCase();

export const skillFocus = (
  config: InterviewerConfig,
  key: string,
): SkillFocus =>
  config.emphasize.some(sameSkill(key))
    ? 'more'
    : config.deemphasize.some(sameSkill(key))
      ? 'less'
      : 'neutral';

/**
 * Neutral, then more focus, then less focus, then back to neutral. The skill
 * is taken out of both lists first, however it was spelled, so it's never in
 * either twice.
 */
export const cycleSkill = (
  config: InterviewerConfig,
  key: string,
): InterviewerConfig => {
  const current = skillFocus(config, key);
  const emphasize = config.emphasize.filter((k) => !sameSkill(key)(k));
  const deemphasize = config.deemphasize.filter((k) => !sameSkill(key)(k));
  // A state already holding five is skipped, never overfilled.
  const roomMore = emphasize.length < MAX_FOCUS;
  const roomLess = deemphasize.length < MAX_FOCUS;
  if (current === 'neutral') {
    if (roomMore) emphasize.push(key);
    else if (roomLess) deemphasize.push(key);
  }
  if (current === 'more' && roomLess) deemphasize.push(key);
  return { ...config, emphasize, deemphasize };
};

// Rio's rule for a question's id: lowercase letters, digits and dashes, at
// most 24 characters. Ids made here ("q-<time>-<n>") always pass it.
let questionCounter = 0;
export const newQuestionId = () =>
  `q-${Date.now().toString(36)}-${(questionCounter += 1)}`;

/** An id that passes Rio's rule and no question in the config already has. */
export const unusedQuestionId = (config: InterviewerConfig, base: string) => {
  const taken = new Set(config.customQuestions.map((q) => q.id));
  let id = base;
  for (let n = 2; taken.has(id); n += 1) id = `${base}-${n}`;
  return id;
};

/** The sections a question can be asked in: the team's own sections that are on. */
export const questionSections = (config: InterviewerConfig) =>
  config.sections.filter((s) => s.enabled && QUESTION_SECTIONS.includes(s.key));

export const addQuestion = (
  config: InterviewerConfig,
  text: string,
  section: SectionKey,
  id: string = newQuestionId(),
): InterviewerConfig => {
  const clean = text.trim().slice(0, MAX_QUESTION_LENGTH);
  if (!clean || config.customQuestions.length >= MAX_QUESTIONS) return config;
  // Rio writes the bar (area and expectations) once it sees the question, and
  // its bar decides then whether it counts (setExpectations, or Rio on save if
  // it's saved first). Until then it's kept as said, so the config stays one
  // Rio can run.
  const question: CustomQuestion = {
    id,
    text: clean,
    section,
    followUp: true,
    area: null,
    areaLabel: null,
    expectations: null,
    counts: false,
    countsFromBar: true,
  };
  return { ...config, customQuestions: [...config.customQuestions, question] };
};

/** Rio's bar holds 3 to 5 excellent signals, 1 to 3 adequate and 1 to 3 weak. */
export const MAX_SIGNALS: Record<ExpectationLevel, number> = {
  excellent: 5,
  adequate: 3,
  weak: 3,
};
/** Rio's limit for one signal of a bar. */
export const MAX_SIGNAL_LENGTH = 140;

const cleanSignals = (items: string[], level: ExpectationLevel) =>
  items
    .map((t) => t.trim().slice(0, MAX_SIGNAL_LENGTH))
    .filter(Boolean)
    .slice(0, MAX_SIGNALS[level]);

export const updateQuestion = (
  config: InterviewerConfig,
  id: string,
  patch: Partial<
    Pick<CustomQuestion, 'text' | 'section' | 'area' | 'areaLabel' | 'counts'>
  >,
): InterviewerConfig => {
  const text =
    patch.text === undefined
      ? undefined
      : patch.text.trim().slice(0, MAX_QUESTION_LENGTH);
  if (text === '') return config;
  return {
    ...config,
    customQuestions: config.customQuestions.map((q) =>
      q.id === id
        ? {
            ...q,
            ...(text === undefined ? {} : { text }),
            ...(patch.section ? { section: patch.section } : {}),
            ...(patch.area !== undefined
              ? { area: patch.area, areaLabel: patch.areaLabel ?? null }
              : {}),
            ...(patch.counts !== undefined
              ? { counts: patch.counts, countsFromBar: undefined }
              : {}),
          }
        : q,
    ),
  };
};

/** The bar Rio sketched, or one the recruiter edited: a level's signals replaced. */
export const setExpectationLevel = (
  config: InterviewerConfig,
  id: string,
  level: ExpectationLevel,
  items: string[],
): InterviewerConfig => ({
  ...config,
  customQuestions: config.customQuestions.map((q) =>
    q.id === id
      ? {
          ...q,
          expectations: {
            excellent: [],
            adequate: [],
            weak: [],
            ...q.expectations,
            [level]: cleanSignals(items, level),
          },
          // A bar the recruiter writes leaves counting to them.
          countsFromBar: undefined,
        }
      : q,
  ),
});

/**
 * Rio's bar for a question, when it arrives (draft_bar), filling only what's
 * missing, as Rio does on save: its signals; its area unless the recruiter
 * chose one; and whether it counts only for a question just added (a complete
 * bar counts), never one the recruiter or a saved version set. A question
 * that has a bar by now (the recruiter started one) is left as it is, and a
 * reply with no bar changes nothing.
 */
export const setExpectations = (
  config: InterviewerConfig,
  id: string,
  bar: Pick<DraftedBar, 'area' | 'areaLabel' | 'expectations' | 'counts'>,
): InterviewerConfig => {
  const { expectations } = bar;
  if (!expectations) return config;
  return {
    ...config,
    customQuestions: config.customQuestions.map((q) =>
      q.id === id && !q.expectations
        ? {
            ...q,
            expectations: {
              excellent: cleanSignals(expectations.excellent, 'excellent'),
              adequate: cleanSignals(expectations.adequate, 'adequate'),
              weak: cleanSignals(expectations.weak, 'weak'),
            },
            area: q.area ?? bar.area,
            areaLabel: q.area ? q.areaLabel : bar.areaLabel,
            counts: q.countsFromBar ? bar.counts : q.counts,
            countsFromBar: undefined,
          }
        : q,
    ),
  };
};

export const removeQuestion = (
  config: InterviewerConfig,
  id: string,
): InterviewerConfig => ({
  ...config,
  customQuestions: config.customQuestions.filter((q) => q.id !== id),
});

export const addAvoidTopic = (
  config: InterviewerConfig,
  topic: string,
): InterviewerConfig => {
  const clean = topic.trim().slice(0, MAX_AVOID_TOPIC_LENGTH);
  if (
    !clean ||
    config.avoidTopics.length >= MAX_AVOID_TOPICS ||
    config.avoidTopics.some((t) => t.toLowerCase() === clean.toLowerCase())
  ) {
    return config;
  }
  return { ...config, avoidTopics: [...config.avoidTopics, clean] };
};

export const removeAvoidTopic = (
  config: InterviewerConfig,
  topic: string,
): InterviewerConfig => ({
  ...config,
  avoidTopics: config.avoidTopics.filter((t) => t !== topic),
});

export const setTone = (
  config: InterviewerConfig,
  tone: Tone,
): InterviewerConfig => ({
  ...config,
  tone,
});

/**
 * The work tasks a config asks for, in order. A config without `kinds` (one
 * built before several tasks) is just its kind, as Rio reads it.
 */
export const kindsOf = (config: InterviewerConfig): WorkspaceKind[] =>
  config.workspace.kinds?.length
    ? config.workspace.kinds
    : [config.workspace.kind];

/**
 * The work task's switch, kind and kinds. `kind` and `kinds` move together:
 * naming `kinds` sets `kind` to the first, and naming `kind` alone narrows the
 * set to it (what an older Rio, which reads only `kind`, would do).
 */
export const setWorkspace = (
  config: InterviewerConfig,
  patch: Partial<InterviewerConfig['workspace']>,
): InterviewerConfig => {
  const named = patch.kinds?.length ? patch.kinds : null;
  const kinds = named ?? (patch.kind ? [patch.kind] : kindsOf(config));
  const workspace = {
    ...config.workspace,
    ...patch,
    kind: kinds[0],
    kinds,
  };
  const hasSection = config.sections.some((s) => s.key === 'workspace');
  const sectionOn = config.sections.find((s) => s.key === 'workspace')?.enabled;
  const next = { ...config, workspace };
  // The work task is also a section of the interview; keep the two in step,
  // and leave both as they were when the section can't change.
  if (
    hasSection &&
    patch.enabled !== undefined &&
    patch.enabled !== sectionOn
  ) {
    const toggled = toggleSection(next, 'workspace');
    return toggled === next ? config : { ...toggled, workspace };
  }
  return next;
};

const sameList = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((item, i) => item === b[i]);

/**
 * Adds or removes a kind from the set of work tasks, as the chips do: a kind
 * goes last, `auto` clears the others, a named kind clears `auto`, and at
 * least one stays. Unchanged when nothing would change.
 */
export const toggleWorkspaceKind = (
  config: InterviewerConfig,
  kind: WorkspaceKind,
): InterviewerConfig => {
  const current = kindsOf(config);
  const kinds: WorkspaceKind[] =
    kind === 'auto'
      ? ['auto']
      : current.includes(kind)
        ? current.filter((k) => k !== kind)
        : [...current.filter((k) => k !== 'auto'), kind];
  if (!kinds.length || sameList(kinds, current)) return config;
  return setWorkspace(config, { kinds });
};

/** Moves a chosen kind earlier (-1) or later (+1) in the order the tasks run. */
export const moveWorkspaceKind = (
  config: InterviewerConfig,
  kind: WorkspaceKind,
  delta: -1 | 1,
): InterviewerConfig => {
  const kinds = [...kindsOf(config)];
  const from = kinds.indexOf(kind);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= kinds.length) return config;
  [kinds[from], kinds[to]] = [kinds[to], kinds[from]];
  return setWorkspace(config, { kinds });
};

/** What Rio gives a work task today (its fixed 15 minutes), until its preview says. */
export const DEFAULT_TASK_MINUTES = 15;

/**
 * The minutes one task of this kind takes. Rio's preview lists the tasks it
 * would open (each with its own minutes); for a kind it hasn't listed, the
 * first it did list stands, and with none, today's 15.
 */
export const taskMinutes = (
  kind: WorkspaceKind,
  workspace: InterviewerPreview['workspace'] | null,
): number => {
  const tasks = workspace?.tasks ?? [];
  return (
    tasks.find((t) => t.kind === kind)?.minutes ??
    tasks[0]?.minutes ??
    DEFAULT_TASK_MINUTES
  );
};

/**
 * The minutes a set of kinds needs from the work space: each kind's task, added
 * up, as Rio checks it on save (a kind Rio has no task for in the role still
 * counts there).
 */
export const setMinutes = (
  kinds: readonly WorkspaceKind[],
  workspace: InterviewerPreview['workspace'] | null,
): number => kinds.reduce((sum, k) => sum + taskMinutes(k, workspace), 0);

/**
 * Gives the work space `minutes` when it has fewer: its section grows to that
 * and the interview with it, up to the interview's own limit. Past that, the
 * other sections share what's left (they are rescaled, as moving the length
 * does), each keeping at least a minute. Null when even that can't hold it.
 * Unchanged when the work space is off or already has enough.
 */
export const giveWorkspaceMinutes = (
  config: InterviewerConfig,
  minutes: number,
): InterviewerConfig | null => {
  const section = config.sections.find((s) => s.key === 'workspace');
  if (!section?.enabled || section.minutes >= minutes) return config;
  const others = config.sections.filter((s) => s.enabled && s !== section);
  const total = enabledTotal(config.sections) - section.minutes + minutes;
  const grow = (s: InterviewerSection) =>
    s.key === 'workspace' ? { ...s, minutes } : s;
  if (total <= MAX_TOTAL) {
    return {
      ...config,
      sections: config.sections.map(grow),
      targetMinutes: Math.max(config.targetMinutes, total),
    };
  }
  const room = MAX_TOTAL - minutes;
  if (room < others.length) return null;
  const rescaled = rebalance(
    config.sections.map((s) =>
      s.key === 'workspace' ? { ...s, enabled: false } : s,
    ),
    room,
  );
  return {
    ...config,
    sections: rescaled.map((s) =>
      s.key === 'workspace' ? { ...section, minutes } : s,
    ),
    targetMinutes: MAX_TOTAL,
  };
};

/** What the work space's minutes were set to by the card, to put back or to compare. */
export interface MinutesSnapshot {
  sections: InterviewerSection[];
  targetMinutes: number;
}

export const minutesOf = (config: InterviewerConfig): MinutesSnapshot => ({
  sections: config.sections,
  targetMinutes: config.targetMinutes,
});

/** Whether no section's minutes or switch, nor the interview's length, differ. */
export const sameMinutes = (a: MinutesSnapshot, b: MinutesSnapshot) =>
  a.targetMinutes === b.targetMinutes &&
  a.sections.length === b.sections.length &&
  a.sections.every(
    (s, i) =>
      s.key === b.sections[i].key &&
      s.minutes === b.sections[i].minutes &&
      s.enabled === b.sections[i].enabled,
  );

/** Puts minutes back as they were, leaving the order and switches of the sections as they are now. */
export const withMinutes = (
  config: InterviewerConfig,
  minutes: MinutesSnapshot,
): InterviewerConfig => ({
  ...config,
  targetMinutes: minutes.targetMinutes,
  sections: config.sections.map((s) => {
    const was = minutes.sections.find((m) => m.key === s.key);
    return was ? { ...s, minutes: was.minutes } : s;
  }),
});

/**
 * Each section whose minutes differ, as "Skills 14 to 9 min", in the order of
 * the interview: what the card changed to fit the work tasks, for it to say.
 */
export const describeMinuteChanges = (
  before: MinutesSnapshot,
  after: MinutesSnapshot,
  titleOf: (key: SectionKey) => string,
): string[] =>
  after.sections.flatMap((s) => {
    const was = before.sections.find((b) => b.key === s.key);
    return was && was.minutes !== s.minutes
      ? [`${titleOf(s.key)} ${was.minutes} to ${s.minutes} min`]
      : [];
  });

/** The minutes Rio says to give the work space ("Give it 30 minutes"), or null. */
export const minutesToGiveFrom = (message: string): number | null => {
  const match = /Give it (\d+) minutes/.exec(message);
  return match ? Number(match[1]) : null;
};

/** The most a section can have, as Rio's refusal says it ("a section can have at most 30"). */
export const sectionLimitFrom = (message: string): number | null => {
  const match = /at most (\d+)/.exec(message);
  return match ? Number(match[1]) : null;
};

export const WORKSPACE_LABELS: Record<WorkspaceKind, string> = {
  auto: 'Rio picks',
  code: 'Code',
  whiteboard: 'Whiteboard',
  design: 'Design canvas',
};

/**
 * Whether a kind of work task doesn't open yet, so the card marks it "Soon"
 * and says what Rio does meanwhile. Rio's preview lists the kinds that open
 * (`availableKinds`: the canvases too once its canvases switch is on). When it
 * doesn't say, from an older Rio or before any preview, the whiteboard and
 * the design canvas are taken as not open yet, as they always were.
 */
export const isComingSoon = (
  kind: WorkspaceKind,
  availableKinds: readonly WorkspaceKind[] | null,
): boolean =>
  availableKinds
    ? !availableKinds.includes(kind)
    : kind === 'whiteboard' || kind === 'design';

/**
 * Rio's notes with the work task's swapped for the picked kind's, in the place
 * Rio puts them (after the AI questions, before the team's own questions):
 * where the first of its own task notes was. When Rio's list had none, say it
 * was made with the task off, that place isn't known and they go last.
 */
const withTaskNotes = (
  notes: string[],
  everyTaskNote: ReadonlySet<string>,
  taskNotes: string[],
): string[] => {
  const at = notes.findIndex((n) => everyTaskNote.has(n));
  const rest = notes.filter((n) => !everyTaskNote.has(n));
  return at < 0
    ? [...rest, ...taskNotes]
    : [...rest.slice(0, at), ...taskNotes, ...rest.slice(at)];
};

/**
 * Rio's notes on the set as a whole (a kind it has no task for, a set that
 * needs more minutes than the work space has). They belong to the set Rio
 * previewed, so a changed set drops them rather than show them stale.
 */
const SET_NOTE =
  /^(These work tasks need \d+ minutes|Rio has no .+ task it can open for this role, so it opens the others you chose)/;

/** Rio's note on a kind (the last it lists, as `workspace.note` is), or null. */
const lastNote = (
  notesByKind: Record<WorkspaceKind, string[]> | null,
  kind: WorkspaceKind,
) => {
  const notes = notesByKind?.[kind] ?? [];
  return notes[notes.length - 1] ?? null;
};

/**
 * The tasks Rio would open for the set on the page, with several tasks on and
 * the work task on. For the set Rio previewed, exactly its list. For a set the
 * recruiter has changed since, each kind's task from that list when Rio has it;
 * a kind Rio read and left out (no task for this role) stays out; and a kind
 * Rio hasn't looked at is taken to open, at the minutes Rio gives a task
 * (taskMinutes), until the next preview says.
 */
export const deriveTasks = (
  config: InterviewerConfig,
  base: InterviewerPreview | null,
): WorkspaceTask[] => {
  const ws = base?.workspace ?? null;
  if (!config.workspace.enabled) return [];
  const read = !!ws?.multi && ws.enabled;
  if (read && ws?.tasks && sameList(ws.kinds ?? [], kindsOf(config))) {
    return ws.tasks;
  }
  return kindsOf(config).flatMap((kind): WorkspaceTask[] => {
    const known = ws?.tasks ?? [];
    // `auto` opens whatever Rio picks: its one task, or none for the role.
    const own =
      kind === 'auto'
        ? read
          ? known.slice(0, 1)
          : []
        : known.filter((t) => t.kind === kind);
    if (own.length) return own;
    if (read && ws?.kinds?.includes(kind)) return [];
    return [
      {
        kind,
        label: WORKSPACE_LABELS[kind],
        minutes: taskMinutes(kind, ws),
        note: lastNote(ws?.notesByKind ?? null, kind),
      },
    ];
  });
};

/**
 * The candidate's-eye preview for the config on the page. Rio's last preview
 * supplies the titles, summaries and example questions; the order, minutes,
 * switches and the recruiter's own questions come from the config, so the
 * preview follows every local edit at once without a request.
 */
export const derivePreview = (
  config: InterviewerConfig,
  base: InterviewerPreview | null,
  skills: PostingSkill[],
): InterviewerPreview => {
  const byKey = new Map(base?.sections.map((s) => [s.key, s]) ?? []);
  const sections: PreviewSection[] = config.sections.map((s) => {
    const fromRio = byKey.get(s.key);
    return {
      key: s.key,
      title: fromRio?.title || SECTION_META[s.key].title,
      summary: fromRio?.summary || SECTION_META[s.key].summary,
      exampleQuestions: fromRio?.exampleQuestions ?? [],
      minutes: s.minutes,
      enabled: s.enabled,
    };
  });
  const label = (key: string) =>
    skills.find((s) => s.key === key)?.label ??
    base?.emphasis.emphasize.find((s) => s.key === key)?.label ??
    base?.emphasis.deemphasize.find((s) => s.key === key)?.label ??
    key;
  // Which kinds open depends on where Rio runs, not on the config: Rio's last
  // preview says, whichever kind it was for.
  const availableKinds = base?.workspace.availableKinds ?? null;
  // Rio's notes on the work task, for the task the recruiter has picked now.
  // Rio lists every kind's (notesByKind), so a change of kind or on/off needs
  // no request: its notes for the others come out, the picked kind's go in.
  // An older Rio doesn't list them: then Rio's note stands for the kind it was
  // written for, and the page's own note marks a kind that doesn't open yet.
  const notesByKind = base?.workspace.notesByKind ?? null;
  const multi = base?.workspace.multi ?? false;
  const tasks = multi ? deriveTasks(config, base) : null;
  // With several tasks, every chosen kind's notes, each once, in the order of
  // the set; Rio's own notes stand when the set is the one it previewed.
  const taskNotes =
    notesByKind && config.workspace.enabled
      ? multi
        ? kindsOf(config)
            .flatMap((kind) => notesByKind[kind])
            .filter((note, i, all) => all.indexOf(note) === i)
        : notesByKind[config.workspace.kind]
      : [];
  const asRioPreviewed =
    multi &&
    base?.workspace.enabled === config.workspace.enabled &&
    sameList(base?.workspace.kinds ?? [], kindsOf(config));
  const everyTaskNote = new Set(
    notesByKind ? WORKSPACE_KINDS.flatMap((kind) => notesByKind[kind]) : [],
  );
  return {
    totalMinutes: enabledTotal(config.sections),
    sections,
    workspace: {
      enabled: config.workspace.enabled,
      kind: config.workspace.kind,
      label:
        tasks && tasks.length > 1
          ? `${tasks.length} work tasks: ${tasks.map((t) => t.label.toLowerCase()).join(', ')}`
          : base?.workspace.kind === config.workspace.kind
            ? base.workspace.label
            : WORKSPACE_LABELS[config.workspace.kind],
      note: asRioPreviewed
        ? (base?.workspace.note ?? null)
        : notesByKind
          ? (taskNotes[taskNotes.length - 1] ?? null)
          : isComingSoon(config.workspace.kind, availableKinds)
            ? (base?.workspace.kind === config.workspace.kind &&
                base.workspace.note) ||
              'Coming soon. Until then Rio uses a code task, or none if the role has no code.'
            : null,
      availableKinds,
      notesByKind,
      multi,
      kinds: multi ? kindsOf(config) : null,
      tasks,
      tasksMinutes: tasks ? tasks.reduce((sum, t) => sum + t.minutes, 0) : null,
    },
    emphasis: {
      emphasize: config.emphasize.map((key) => ({ key, label: label(key) })),
      deemphasize: config.deemphasize.map((key) => ({
        key,
        label: label(key),
      })),
    },
    customQuestions: config.customQuestions.map(
      ({ id, text, section, area, areaLabel, expectations, counts }) => ({
        id,
        text,
        section,
        area,
        // A saved config carries the area's key alone; Rio's list names it.
        areaLabel:
          areaLabel ?? base?.areas.find((a) => a.key === area)?.label ?? null,
        expectations,
        counts,
      }),
    ),
    notes: asRioPreviewed
      ? (base?.notes ?? [])
      : notesByKind
        ? withTaskNotes(
            (base?.notes ?? []).filter((n) => !(multi && SET_NOTE.test(n))),
            everyTaskNote,
            taskNotes,
          )
        : (base?.notes ?? []),
    areas: base?.areas ?? [],
  };
};

/**
 * Which parts of the card a plain-language edit changed, so they can light up
 * briefly. Keys match the `data-region` names the card puts on each part.
 */
export const changedRegions = (
  diff: DiffEntry[],
  config: InterviewerConfig,
): Set<string> => {
  const regions = new Set<string>();
  for (const { path, before, after } of diff) {
    const sectionMatch = /^sections\[(\d+)\]/.exec(path);
    if (sectionMatch) {
      const section = config.sections[Number(sectionMatch[1])];
      if (section) regions.add(`section:${section.key}`);
      regions.add('sections');
      continue;
    }
    if (path === 'sections') {
      regions.add('sections');
      continue;
    }
    if (path.startsWith('targetMinutes')) regions.add('length');
    else if (path.startsWith('emphasize') || path.startsWith('deemphasize')) {
      regions.add('skills');
      const keys = new Set([
        ...(Array.isArray(before) ? before : []),
        ...(Array.isArray(after) ? after : []),
      ]);
      const was = new Set(Array.isArray(before) ? before : []);
      const now = new Set(Array.isArray(after) ? after : []);
      keys.forEach((k) => {
        if (typeof k === 'string' && was.has(k) !== now.has(k))
          regions.add(`skill:${k}`);
      });
    } else if (path.startsWith('customQuestions')) regions.add('questions');
    else if (path.startsWith('avoidTopics')) regions.add('avoid');
    else if (path.startsWith('tone')) regions.add('tone');
    else if (path.startsWith('workspace')) regions.add('workspace');
  }
  return regions;
};

export const sameConfig = (
  a: InterviewerConfig | null,
  b: InterviewerConfig | null,
) => {
  if (!a || !b) return a === b;
  const strip = (c: InterviewerConfig) => ({ ...c, meta: null });
  return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
};

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/**
 * One line saying what a saved version changed against the one before, for
 * the version history. Rio sends the configs; the words are worked out here.
 */
export const summarizeChange = (
  before: InterviewerConfig | null,
  after: InterviewerConfig,
): string => {
  if (!before) return 'Started from Rio’s draft';
  const parts: string[] = [];
  const title = (key: SectionKey) => SECTION_META[key].title;
  if (before.targetMinutes !== after.targetMinutes) {
    parts.push(`length to ${after.targetMinutes} minutes`);
  }
  const was = new Map(before.sections.map((s) => [s.key, s]));
  after.sections.forEach((s) => {
    const prev = was.get(s.key);
    if (prev && prev.enabled !== s.enabled) {
      parts.push(`${s.enabled ? 'added' : 'skipped'} ${title(s.key)}`);
    }
  });
  const order = (c: InterviewerConfig) => c.sections.map((s) => s.key).join();
  if (order(before) !== order(after)) parts.push('reordered the sections');
  const focus = (c: InterviewerConfig) =>
    [...c.emphasize, '|', ...c.deemphasize].join();
  if (focus(before) !== focus(after)) parts.push('changed skill focus');
  const added = after.customQuestions.length - before.customQuestions.length;
  if (added > 0) parts.push(`added ${plural(added, 'question', 'questions')}`);
  else if (added < 0)
    parts.push(`removed ${plural(-added, 'question', 'questions')}`);
  else if (
    JSON.stringify(before.customQuestions) !==
    JSON.stringify(after.customQuestions)
  ) {
    parts.push('edited questions');
  }
  if (before.tone !== after.tone) parts.push(`tone to ${after.tone}`);
  if (JSON.stringify(before.workspace) !== JSON.stringify(after.workspace)) {
    const several = kindsOf(before).length > 1 || kindsOf(after).length > 1;
    parts.push(
      after.workspace.enabled
        ? `changed the work ${several ? 'tasks' : 'task'}`
        : 'removed the work task',
    );
  }
  if (before.avoidTopics.join() !== after.avoidTopics.join())
    parts.push('changed topics to avoid');
  if (!parts.length) return 'Saved with no changes';
  const line = parts.join('; ');
  return line.charAt(0).toUpperCase() + line.slice(1);
};

/**
 * Rio's problems with a config that saving it would still leave. Save writes
 * the bar for a question that counts with no bar yet, so Rio's "needs a
 * complete bar" for that question isn't one; everything else is.
 */
export const problemsSaveLeaves = (
  config: InterviewerConfig,
  problems: FieldError[],
): FieldError[] =>
  problems.filter((p) => {
    const match = /^customQuestions\[(\d+)\]$/.exec(p.path);
    const question = match
      ? config.customQuestions[Number(match[1])]
      : undefined;
    return !(question && !question.expectations);
  });

/** Rio's longest skill name. */
const MAX_SKILL_NAME = 60;

/**
 * A skill's key is its name exactly as Rio stores it in emphasis: the label
 * with its spaces collapsed, cut at 60 characters (posting_skills in
 * agents/interview/interviewer_config.py). Rio drafts and edits emphasis with
 * these names, so a chip keyed this way shows Rio's own choice.
 */
export const skillKey = (label: string) =>
  Array.from(label.split(/\s+/).filter(Boolean).join(' '))
    .slice(0, MAX_SKILL_NAME)
    .join('');

const ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

/** The editor writes HTML; Rio drafts from the words. */
export const stripHtml = (html: string) =>
  html
    .replace(/<(br|\/p|\/li|\/h\d)[^>]*>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(nbsp|amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m] ?? m)
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .trim();

export const postingFrom = ({
  title,
  description,
  requiredSkills,
  preferredSkills,
}: {
  title?: string | null;
  description?: string | null;
  requiredSkills?: string[] | null;
  preferredSkills?: string[] | null;
}) => {
  const skills: PostingSkill[] = [];
  const seen = new Set<string>();
  const add = (labels: string[] | null | undefined, required: boolean) =>
    (labels ?? []).forEach((raw) => {
      const label = String(raw).trim();
      const key = skillKey(label);
      // Named once, as Rio keeps them: the first spelling wins.
      if (!label || !key || seen.has(key.toLowerCase())) return;
      seen.add(key.toLowerCase());
      skills.push({ key, label, required });
    });
  add(requiredSkills, true);
  add(preferredSkills, false);
  return {
    title: (title ?? '').trim(),
    description: stripHtml(description ?? ''),
    skills,
  };
};

/** Enough of a posting for Rio to draft something worth showing. */
export const postingReady = (posting: { title: string; description: string }) =>
  posting.title.length > 0 && posting.description.length >= 40;
